/**
 * 使用说明模块
 * 提供三大模块的详细使用指南
 */
import './guide.css';

// 意窗指南数据
interface GuideSection {
  id: string;
  icon: string;
  title: string;
  items: {
    title: string;
    desc: string;
    tips?: string[];
  }[];
}

const ALGORITHMS_GUIDE: GuideSection[] = [
  {
    id: 'algorithm-list',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 6h16M4 12h16M4 18h16"/></svg>',
    title: '算法列表',
    items: [
      {
        title: '选择算法',
        desc: '在左侧面板中，算法按照类型分组展示。点击任意算法按钮，即可在中间3D区域查看该算法的可视化演示。',
        tips: [
          '排序算法：冒泡、选择、插入、快速、归并、堆排序等9种',
          '搜索算法：线性查找、二分查找、跳跃查找',
          '数据结构：链表、栈、队列、二叉树、哈希表',
          '图算法：BFS、DFS、最短路径、最小生成树、拓扑排序',
          '动态规划：斐波那契、背包问题、LCS、零钱兑换等',
          '经典算法：汉诺塔、N皇后、迷宫求解、数独等',
          '字符串：KMP匹配、Rabin-Karp、编辑距离',
          '数学算法：最大公约数、素数筛法、快速幂'
        ]
      },
      {
        title: '算法分类',
        desc: '共有8大分类，涵盖30+种经典算法。每个分类下的算法按照难度和相关性排列，方便系统性学习。'
      }
    ]
  },
  {
    id: '3d-visualization',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>',
    title: '3D可视化展示',
    items: [
      {
        title: '3D动画演示',
        desc: '中间区域是3D可视化舞台，使用WebGL技术渲染。算法执行过程会以动画形式展现，让抽象的数据结构和算法过程变得直观可见。',
        tips: [
          '不同算法使用不同的3D模型风格（柱状、立方体、节点等）',
          '比较、交换、访问等操作都有对应的动画效果',
          '元素会用不同颜色标识当前状态'
        ]
      },
      {
        title: '视角控制',
        desc: '支持手势交互控制3D视角，全方位观察数据结构。',
        tips: [
          '单指拖动：旋转视角',
          '双指捏合：缩放视图',
          '双指拖动：平移视图'
        ]
      },
      {
        title: '步骤指示器',
        desc: '3D区域底部显示当前执行的步骤编号和操作说明，帮助理解每一步算法在做什么。'
      }
    ]
  },
  {
    id: 'playback-control',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>',
    title: '播放控制',
    items: [
      {
        title: '控制按钮',
        desc: '右侧"运行控制"面板提供完整的播放控制功能，让你自由掌控算法演示进度。',
        tips: [
          '重新开始：回到算法第一步，从头开始演示',
          '上一步：回退一步，查看之前的状态',
          '播放/暂停：自动播放或暂停动画',
          '下一步：执行下一步操作',
          '看结果：直接跳到算法执行完成状态'
        ]
      },
      {
        title: '速度调节',
        desc: '通过速度滑块调整动画播放速度，范围0.25x到4x。初学者可以放慢速度仔细观察，熟悉后可以加速查看。'
      }
    ]
  },
  {
    id: 'watch-window',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/></svg>',
    title: '观察窗',
    items: [
      {
        title: '变量监视',
        desc: '右侧"观察窗"面板实时显示算法执行过程中的关键变量值，帮助理解数据变化。',
        tips: [
          '显示当前索引、比较值、交换情况等',
          '数组状态会实时更新',
          '辅助变量（如pivot、left、right）也会显示'
        ]
      }
    ]
  },
  {
    id: 'log-output',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>',
    title: '日志输出',
    items: [
      {
        title: '执行日志',
        desc: '右侧底部的"日志输出"面板记录算法执行过程中的关键操作日志，包含操作类型和具体信息。',
        tips: [
          '支持不同类型日志：信息(蓝)、成功(绿)、警告(黄)、错误(红)',
          '点击清空按钮可清除所有日志'
        ]
      }
    ]
  },
  {
    id: 'algorithm-code',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>',
    title: '算法代码',
    items: [
      {
        title: '代码查看',
        desc: '底部左侧"算法代码"面板展示当前算法的完整实现代码，帮助理解算法逻辑。',
        tips: [
          '代码带有语法高亮，便于阅读',
          '选择算法后自动更新代码展示'
        ]
      },
      {
        title: '多语言切换',
        desc: '支持三种编程语言的代码实现：JavaScript、Python、C++。点击顶部语言标签即可切换。',
        tips: [
          'JS：适合前端开发者和Node.js用户',
          'Python：简洁易读，适合算法学习',
          'C++：面试常用，性能最优'
        ]
      }
    ]
  },
  {
    id: 'algorithm-intro',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>',
    title: '算法介绍',
    items: [
      {
        title: '算法详情',
        desc: '底部右侧"算法介绍"面板展示当前算法的详细信息，帮助全面了解算法特性。',
        tips: [
          '综合评价：对算法的总体描述和使用建议',
          '时间复杂度：算法执行时间随数据规模增长的量级',
          '空间复杂度：算法所需额外内存空间',
          '应用场景：算法的实际使用场景',
          '优点：算法的主要优势',
          '缺点：算法的局限性'
        ]
      }
    ]
  }
];

// 编程指南数据
const PRACTICE_GUIDE: GuideSection[] = [
  {
    id: 'practice-home',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>',
    title: '大厅首页',
    items: [
      {
        title: '题目卡片',
        desc: '首页展示所有可用的编程题目，以卡片形式呈现。每张卡片显示题目名称、难度标签和收藏状态。',
        tips: [
          '点击卡片进入做题页面',
          '点击卡片上的星标可收藏/取消收藏题目',
          '已完成的题目卡片会显示对勾标记'
        ]
      },
      {
        title: '筛选功能',
        desc: '顶部筛选栏支持按难度和分类快速筛选题目，帮助找到目标练习内容。',
        tips: [
          '难度筛选：全部、简单、中等、困难',
          '分类筛选：数组、字符串、链表、树、动态规划'
        ]
      }
    ]
  },
  {
    id: 'code-editor',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>',
    title: '代码编辑器',
    items: [
      {
        title: '界面布局',
        desc: '做题页面采用左右分栏布局，左侧显示题目描述和示例，右侧是代码编辑器，底部是运行结果面板。',
        tips: [
          '题目描述包含输入输出说明和示例',
          '点击左上角返回按钮可回到题库'
        ]
      },
      {
        title: '语言选择',
        desc: '编辑器顶部提供语言选择下拉框，支持四种主流编程语言。',
        tips: [
          'JavaScript：前端开发者首选，可本地运行',
          'Python：简洁易学，算法入门推荐',
          'Java：企业开发主流语言',
          'C++：竞赛和面试常用'
        ]
      },
      {
        title: '编辑器功能',
        desc: '基于CodeMirror的专业代码编辑器，提供丰富的编辑功能。',
        tips: [
          '语法高亮：不同代码元素显示不同颜色',
          '行号显示：左侧显示行号便于定位',
          '括号匹配：自动高亮配对的括号',
          '自动补全：输入时自动提示代码补全',
          '光标行高亮：当前编辑行突出显示'
        ]
      },
      {
        title: '编辑器设置',
        desc: '可在“设置”模块的“编辑器”页签中自定义编辑器外观和行为。',
        tips: [
          '主题选择：Dracula、Monokai等多种主题',
          '字体大小：12px-24px可调',
          '行号开关：可隐藏行号扩大编辑区',
          '换行模式：自动换行或横向滚动',
          '设置修改后需重启应用生效'
        ]
      },
      {
        title: '运行代码',
        desc: '点击“运行代码”按钮执行当前代码，结果展示在底部运行结果面板。',
        tips: [
          'JavaScript可本地运行，无需网络',
          '其他语言通过Wandbox云编译执行',
          '运行结果显示输出内容或错误信息'
        ]
      },
      {
        title: '查看答案',
        desc: '点击“查看答案”按钮可查看题目的参考答案和解题思路。',
        tips: [
          '建议先独立思考，实在困难时再查看',
          '答案仅供参考，解题方法不唯一'
        ]
      }
    ]
  },
  {
    id: 'practice-favorites',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>',
    title: '我的收藏',
    items: [
      {
        title: '收藏管理',
        desc: '集中查看所有已收藏的题目，方便复习和重点练习。顶部统计卡片显示各难度收藏数量。',
        tips: [
          '支持按难度筛选收藏题目',
          '支持按收藏时间或难度排序',
          '点击题目可直接进入做题页面'
        ]
      }
    ]
  },
  {
    id: 'practice-history',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
    title: '我的记录',
    items: [
      {
        title: '学习进度',
        desc: '查看你的整体学习进度和完成情况。统计卡片显示题目总数、已完成数量和完成率。',
        tips: [
          '进度条直观展示完成百分比',
          '已完成题目列表记录你的成就'
        ]
      }
    ]
  },
  {
    id: 'practice-quiz',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>',
    title: '刷题模式',
    items: [
      {
        title: '刷题模式选择',
        desc: '提供多种刷题模式，适应不同的学习需求和场景。',
        tips: [
          '随机刷题：随机抽取题目练习',
          '顺序刷题：按顺序逐题练习',
          '专项练习：选择特定分类集中练习',
          '限时挑战：5道题5分钟，考验速度',
          '错题回顾：重新练习做错的题目',
          '编排刷题：自定义选择题目组成练习集'
        ]
      },
      {
        title: '刷题统计',
        desc: '实时统计今日刷题数据，包括完成题数、正确率和连续正确数。'
      }
    ]
  },
  {
    id: 'practice-analysis',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 20V10M12 20V4M6 20v-6"/></svg>',
    title: '数据分析',
    items: [
      {
        title: '答题统计',
        desc: '可视化展示你的答题数据，分析学习效果和薄弱环节。',
        tips: [
          '按分类查看各类型题目的正确率',
          '识别需要加强练习的算法类型'
        ]
      }
    ]
  },
  {
    id: 'quiz-control',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>',
    title: '刷题控制',
    items: [
      {
        title: '答题操作',
        desc: '刷题模式下，顶部显示刷题控制栏，方便快速操作。',
        tips: [
          '跳过：跳过当前题目，进入下一题',
          '提交：保存当前答题结果并继续',
          '上一题/下一题：在题目间切换',
          '退出刷题：结束本次刷题并查看统计'
        ]
      },
      {
        title: '答题情况面板',
        desc: '左侧面板实时显示每道题的答题状态，用方块标识：绿色正确、红色错误、灰色未作答。'
      }
    ]
  }
];

// 积木指南数据
const BLOCKS_GUIDE: GuideSection[] = [
  {
    id: 'blocks-overview',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>',
    title: '界面概览',
    items: [
      {
        title: '整体布局',
        desc: '积木编程界面分为三个区域：左侧积木分类列表、中间积木工作区、右侧舞台与功能区。',
        tips: [
          '左侧：积木分类列表，点击展开对应积木',
          '中间：Blockly工作区，拖拽拼接积木',
          '右侧：舞台显示、角色管理、代码预览'
        ]
      },
      {
        title: '页签切换',
        desc: '顶部TAB页签支持切换“案例库”和“我的工程”两个视图。',
        tips: [
          '案例库：浏览和学习预设的编程案例',
          '我的工程：编辑和运行自己的积木程序'
        ]
      }
    ]
  },
  {
    id: 'blocks-category',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>',
    title: '积木分类',
    items: [
      {
        title: '分类列表',
        desc: '左侧分类列表展示所有可用的积木类别，点击即可展开对应积木列表。',
        tips: [
          '事件：程序入口和事件触发',
          '控制：循环、等待、条件判断',
          '逻辑：与、或、非运算',
          '运算：数学计算和随机数',
          '输出：打印和文本处理',
          '变量：创建和使用变量',
          '函数：自定义函数块',
          '画笔：绘图命令（落笔、移动、颜色等）'
        ]
      },
      {
        title: '拖拽积木',
        desc: '从左侧展开的积木列表中，将需要的积木拖拽到中间工作区。',
        tips: [
          '长按积木开始拖动',
          '放置到工作区合适位置松开'
        ]
      }
    ]
  },
  {
    id: 'blocks-detail',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>',
    title: '积木详解',
    items: [
      {
        title: '运动类积木',
        desc: '控制角色在舞台上的移动和旋转，使用Scratch风格坐标系（中心0,0）。',
        tips: [
          '移动N步：按当前方向移动指定步数',
          '右转N度：顺时针旋转指定角度',
          '左转N度：逆时针旋转指定角度',
          '移到X,Y：瞬移到指定坐标',
          '移到随机位置：移到舞台内随机位置',
          '设置X/Y：单独设置横坐标或纵坐标',
          '改变X/Y：在当前基础上增减坐标值'
        ]
      },
      {
        title: '外观类积木',
        desc: '控制角色的显示状态和文本输出。',
        tips: [
          '打印：输出文本到显示区域',
          '文本：创建一个文本字符串',
          '合并文本：拼接多个文本',
          '文本长度：获取字符串的字符数',
          '文本为空：检查文本是否为空',
          '查找字符：在文本中查找子串位置',
          '获取字符：获取指定位置的字符',
          '截取文本：获取文本的一部分',
          '大小写转换：转换文本大小写',
          '去除空格：去除文本两端空格',
          '说话N秒：角色显示对话框',
          '显示/隐藏：控制角色可见性'
        ]
      },
      {
        title: '声音类积木',
        desc: '播放声音效果，为程序增加音效反馈。',
        tips: [
          '播放声音：播放预设音效（beep/pop/click）',
          '停止声音：停止所有正在播放的声音',
          '设置音量：调整声音音量大小'
        ]
      },
      {
        title: '事件类积木',
        desc: '程序的入口和触发条件，是所有程序的起点。',
        tips: [
          '当开始时：点击运行按钮时执行',
          '当舞台被点击：点击舞台区域时触发'
        ]
      },
      {
        title: '检测类积木',
        desc: '检测输入和时间信息，用于条件判断。',
        tips: [
          '按键被按下：检测指定按键是否按下',
          '计时器：获取程序运行时间（秒）',
          '重置计时器：将计时器归零'
        ]
      },
      {
        title: '控制类积木',
        desc: '程序流程控制，包括条件判断、循环和等待。',
        tips: [
          '如果...:条件判断，根据条件执行不同代码',
          '重复N次：执行内部代码指定次数',
          '永远重复：无限循环执行',
          '当...重复：条件成立时持续执行',
          '计数循环：从起始值到结束值循环',
          '对每个元素：遍历列表中的每个元素',
          '中断/继续：控制循环的中断和继续',
          '等待N秒：暂停执行指定时间',
          '等待直到：等待条件成立后继续'
        ]
      },
      {
        title: '运算类积木',
        desc: '数学计算和逻辑运算，用于数据处理和条件判断。',
        tips: [
          '数字：输入一个数值',
          '加减乘除：基本四则运算',
          '数学函数：平方根、绝对值、对数等',
          '三角函数：sin/cos/tan等三角运算',
          '数学常量：π、e、黄金分割率等',
          '数字属性：检查偶数/奇数/整数等',
          '取整：四舍五入/向上/向下取整',
          '取余：计算两数相除的余数',
          '限定范围：将数值限制在指定范围内',
          '随机整数：生成指定范围内的随机整数',
          '随机小数：生成0到1之间的随机数',
          '比较运算：=、≠、<、>、≤、≥',
          '逻辑运算：与、或、非',
          '布尔值：真/假常量',
          '三元运算：条件?值1:值2'
        ]
      },
      {
        title: '变量类积木',
        desc: '创建和使用变量存储数据，是编程的基础概念。',
        tips: [
          '创建变量：新建一个命名的变量',
          '设置变量：给变量赋新值',
          '改变变量：在当前值基础上增减',
          '获取变量：读取变量的当前值'
        ]
      },
      {
        title: '列表类积木',
        desc: '列表（数组）操作，用于处理一组数据。',
        tips: [
          '创建空列表：创建一个没有元素的列表',
          '创建列表：用指定元素创建列表',
          '重复元素：创建包含N个相同元素的列表',
          '列表长度：获取列表元素个数',
          '列表为空：检查列表是否没有元素',
          '查找元素：获取元素在列表中的位置',
          '获取元素：获取指定位置的元素',
          '设置元素：修改指定位置的元素',
          '截取列表：获取列表的一部分',
          '拆分/合并：文本与列表相互转换',
          '排序：对列表元素进行排序',
          '反转：逆转列表元素顺序'
        ]
      },
      {
        title: '函数类积木',
        desc: '自定义函数，封装可重复使用的代码块。',
        tips: [
          '定义函数：创建一个新函数',
          '调用函数：执行已定义的函数',
          '带参数函数：定义接受参数的函数',
          '带返回值函数：定义有返回结果的函数'
        ]
      },
      {
        title: '画笔类积木',
        desc: '绘图命令，控制画笔在画布上绘制图形。',
        tips: [
          '落笔：开始绘制，移动时留下轨迹',
          '抬笔：停止绘制，移动时不留痕迹',
          '清空画布：清除所有绘制内容',
          '移动N步：按当前方向移动',
          '移到X,Y：移动到指定坐标',
          '右转N度：顺时针旋转方向',
          '左转N度：逆时针旋转方向',
          '设置颜色：设置画笔绘制颜色',
          '设置笔宽：设置线条粗细',
          '画圆：在当前位置绘制圆形',
          '画矩形：在当前位置绘制矩形',
          '填充模式：切换填充或描边模式'
        ]
      }
    ]
  },
  {
    id: 'blocks-workspace',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>',
    title: '工作区操作',
    items: [
      {
        title: '积木拼接',
        desc: '将积木拖到其他积木的插槽位置，自动吸附形成连接。',
        tips: [
          '上下插槽：顺序执行语句',
          '左右插槽：填充参数值',
          '内部插槽：嵌套语句块'
        ]
      },
      {
        title: '积木管理',
        desc: '对工作区中的积木进行编辑和删除操作。',
        tips: [
          '点击积木选中，再次点击拖动',
          '长按积木唤起操作菜单',
          '拖到左下角垃圾桶区域删除'
        ]
      },
      {
        title: '画布控制',
        desc: '工作区支持缩放和平移，方便查看复杂程序。',
        tips: [
          '双指捧合：缩放工作区',
          '双指拖动：平移画布',
          '工具栏“重置”按钮恢复默认视图'
        ]
      }
    ]
  },
  {
    id: 'blocks-stage',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/></svg>',
    title: '舞台区域',
    items: [
      {
        title: '舞台类型',
        desc: '舞台区域支持三种显示模式，根据程序内容自动切换。',
        tips: [
          '画笔模式：显示绘图画布，执行画笔类积木',
          '角色模式：显示角色动画，执行运动类积木',
          '文字模式：显示输出结果，执行打印类积木'
        ]
      },
      {
        title: '画布工具',
        desc: '画笔模式下，工具栏提供多种辅助功能。',
        tips: [
          '网格线：显示/隐藏参考网格',
          '坐标尺：显示/隐藏坐标刻度',
          '设置起点：自定义绘制起始位置',
          '清空画布：清除所有绘制内容'
        ]
      }
    ]
  },
  {
    id: 'blocks-run',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>',
    title: '运行控制',
    items: [
      {
        title: '执行程序',
        desc: '点击工具栏的“运行”按钮执行当前积木程序。',
        tips: [
          '运行中积木会高亮显示当前执行位置',
          '再次点击可暂停执行',
          '点击“停止”按钮终止程序'
        ]
      },
      {
        title: '执行速度',
        desc: '支持三档执行速度，适应不同学习场景。',
        tips: [
          '快速：无延时，瞬间完成',
          '正常：适中延时，观察执行过程',
          '慢速：较长延时，便于细致学习'
        ]
      }
    ]
  },
  {
    id: 'blocks-sprite',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="10" r="3"/><path d="M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662"/></svg>',
    title: '角色管理',
    items: [
      {
        title: '角色列表',
        desc: '右侧角色列表显示当前项目的所有角色，每个角色拥有独立的积木工作区。',
        tips: [
          '点击角色切换当前编辑对象',
          '默认角色为“小猫”，可添加更多角色',
          '每个角色的积木独立运行'
        ]
      },
      {
        title: '添加角色',
        desc: '点击角色列表底部的“+”按钮添加新角色。',
        tips: [
          '选择喜欢的表情图标',
          '输入角色名称',
          '确认后添加到列表'
        ]
      }
    ]
  },
  {
    id: 'blocks-code',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>',
    title: '代码预览',
    items: [
      {
        title: '查看代码',
        desc: '右侧底部“代码预览”区域实时显示积木程序生成的代码。',
        tips: [
          '随积木变化实时更新',
          '帮助理解积木与代码的对应关系',
          '可作为学习编程语言的辅助'
        ]
      }
    ]
  },
  {
    id: 'blocks-presets',
    icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>',
    title: '案例库',
    items: [
      {
        title: '浏览案例',
        desc: '案例库提供多种预设的编程案例，覆盖不同难度和知识点。',
        tips: [
          '按难度筛选：简单、中等、困难',
          '按类型筛选：输出、循环、变量、条件、算法、画笔'
        ]
      },
      {
        title: '学习案例',
        desc: '点击案例卡片查看详情，包含案例介绍、涉及概念和运行结果。',
        tips: [
          '点击“加载到工作区”将案例导入编辑',
          '可以修改案例并运行查看效果',
          '建议从简单案例开始学习'
        ]
      }
    ]
  }
];

class GuideModule {
  private container: HTMLElement | null = null;
  private initialized = false;
  private currentTab = 'algorithms'; // 'algorithms' | 'practice' | 'blocks'
  private expandedSections: Set<string> = new Set();

  /**
   * 初始化使用说明模块
   */
  init(): void {
    if (this.initialized) return;
    
    this.container = document.getElementById('page-guide');
    if (!this.container) {
      console.warn('使用说明页面容器未找到');
      return;
    }

    this.render();
    this.bindEvents();
    this.initTabIndicator();
    this.initialized = true;
    console.log('✅ 使用说明模块初始化完成');
  }

  /**
   * 渲染使用说明页面内容
   */
  private render(): void {
    if (!this.container) return;

    this.container.innerHTML = `
      <!-- 二级TAB导航 -->
      <div class="guide-tabs">
        <button class="guide-tab active" data-tab="algorithms">意窗指南</button>
        <button class="guide-tab" data-tab="practice">编程指南</button>
        <button class="guide-tab" data-tab="blocks">积木指南</button>
      </div>
      
      <!-- 意窗指南视图 -->
      <div id="guide-view-algorithms" class="guide-view active">
        ${this.renderAlgorithmsGuide()}
      </div>
      
      <!-- 编程指南视图 -->
      <div id="guide-view-practice" class="guide-view">
        ${this.renderPracticeGuide()}
      </div>
      
      <!-- 积木指南视图 -->
      <div id="guide-view-blocks" class="guide-view">
        ${this.renderBlocksGuide()}
      </div>
    `;
  }

  /**
   * 渲染意窗指南
   */
  private renderAlgorithmsGuide(): string {
    return `
      <div class="guide-page">
        <div class="guide-content">
          <div class="guide-header">
            <div class="guide-header-icon">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
            </div>
            <h2>意窗指南</h2>
          </div>
          <div class="guide-sections">
            ${ALGORITHMS_GUIDE.map((section, idx) => this.renderSection(section, idx)).join('')}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * 渲染编程指南
   */
  private renderPracticeGuide(): string {
    return `
      <div class="guide-page">
        <div class="guide-content">
          <div class="guide-header">
            <div class="guide-header-icon">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
              </svg>
            </div>
            <h2>编程指南</h2>
          </div>
          <div class="guide-sections">
            ${PRACTICE_GUIDE.map((section, idx) => this.renderSection(section, idx)).join('')}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * 渲染积木指南
   */
  private renderBlocksGuide(): string {
    return `
      <div class="guide-page">
        <div class="guide-content">
          <div class="guide-header">
            <div class="guide-header-icon">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="3" width="7" height="7"/>
                <rect x="14" y="3" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
              </svg>
            </div>
            <h2>积木指南</h2>
          </div>
          <div class="guide-sections">
            ${BLOCKS_GUIDE.map((section, idx) => this.renderSection(section, idx)).join('')}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * 渲染手风琴章节
   */
  private renderSection(section: GuideSection, index: number): string {
    const isExpanded = this.expandedSections.has(section.id);
    return `
      <div class="guide-section ${isExpanded ? 'expanded' : ''}" data-section="${section.id}" style="animation-delay: ${index * 0.05}s">
        <div class="guide-section-header">
          <div class="guide-section-icon">${section.icon}</div>
          <h3 class="guide-section-title">${section.title}</h3>
          <div class="guide-section-arrow">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>
        </div>
        <div class="guide-section-content">
          <div class="guide-section-inner">
            ${section.items.map(item => this.renderItem(item)).join('')}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * 渲染指南项目
   */
  private renderItem(item: { title: string; desc: string; tips?: string[] }): string {
    return `
      <div class="guide-item">
        <h4 class="guide-item-title">${item.title}</h4>
        <p class="guide-item-desc">${item.desc}</p>
        ${item.tips ? `
          <ul class="guide-item-tips">
            ${item.tips.map(tip => `<li>${tip}</li>`).join('')}
          </ul>
        ` : ''}
      </div>
    `;
  }

  /**
   * 渲染占位符页面
   */
  private renderPlaceholder(moduleName: string, description: string): string {
    return `
      <div class="guide-placeholder">
        <div class="guide-placeholder-icon">
          <svg viewBox="0 0 24 24" width="64" height="64" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </div>
        <h2 class="guide-placeholder-title">${moduleName}指南</h2>
      </div>
    `;
  }

  /**
   * 绑定事件
   */
  private bindEvents(): void {
    if (!this.container) return;

    // TAB 切换
    const tabs = this.container.querySelectorAll('.guide-tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const tabId = target.dataset.tab;
        if (tabId) {
          this.switchTab(tabId);
        }
      });
    });

    // 手风琴折叠
    this.container.addEventListener('click', (e) => {
      const header = (e.target as HTMLElement).closest('.guide-section-header');
      if (header) {
        const section = header.closest('.guide-section') as HTMLElement;
        if (section) {
          const sectionId = section.dataset.section;
          if (sectionId) {
            this.toggleSection(sectionId, section);
          }
        }
      }
    });
  }

  /**
   * 切换手风琴章节
   */
  private toggleSection(sectionId: string, element: HTMLElement): void {
    const isExpanded = this.expandedSections.has(sectionId);
    
    if (isExpanded) {
      this.expandedSections.delete(sectionId);
      element.classList.remove('expanded');
    } else {
      this.expandedSections.add(sectionId);
      element.classList.add('expanded');
    }
  }

  /**
   * 切换TAB
   */
  private switchTab(tabId: string): void {
    if (!this.container) return;
    this.currentTab = tabId;

    // 更新TAB按钮状态
    const tabs = this.container.querySelectorAll('.guide-tab');
    tabs.forEach(tab => {
      tab.classList.toggle('active', tab.getAttribute('data-tab') === tabId);
    });

    // 更新指示器位置
    const activeTab = this.container.querySelector('.guide-tab.active') as HTMLElement;
    if (activeTab) {
      this.updateTabIndicator(activeTab);
    }

    // 更新视图显示
    const views = this.container.querySelectorAll('.guide-view');
    views.forEach(view => {
      const viewEl = view as HTMLElement;
      viewEl.classList.toggle('active', viewEl.id === `guide-view-${tabId}`);
    });
  }

  /**
   * 初始化TAB滑动指示器
   */
  private initTabIndicator(): void {
    if (!this.container) return;
    const tabsContainer = this.container.querySelector('.guide-tabs');
    if (!tabsContainer) return;

    // 创建指示器元素
    let indicator = tabsContainer.querySelector('.guide-tab-indicator') as HTMLElement;
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.className = 'guide-tab-indicator';
      tabsContainer.insertBefore(indicator, tabsContainer.firstChild);
    }

    // 初始化位置
    const initPosition = () => {
      const activeTab = tabsContainer.querySelector('.guide-tab.active') as HTMLElement;
      if (activeTab && activeTab.getBoundingClientRect().width > 0) {
        this.updateTabIndicator(activeTab);
      }
    };

    initPosition();
    setTimeout(initPosition, 100);
    setTimeout(initPosition, 300);
  }

  /**
   * 更新TAB指示器位置
   */
  private updateTabIndicator(activeTab: HTMLElement): void {
    if (!this.container) return;
    const tabsContainer = this.container.querySelector('.guide-tabs') as HTMLElement;
    const indicator = tabsContainer?.querySelector('.guide-tab-indicator') as HTMLElement;
    if (!tabsContainer || !indicator) return;

    const containerRect = tabsContainer.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();

    const left = tabRect.left - containerRect.left;
    const width = tabRect.width;

    indicator.style.left = `${left}px`;
    indicator.style.width = `${width}px`;
  }

  /**
   * 刷新页面（切换到使用说明页时调用）
   */
  refresh(): void {
    // 重新初始化指示器位置
    const activeTab = this.container?.querySelector('.guide-tab.active') as HTMLElement;
    if (activeTab) {
      this.updateTabIndicator(activeTab);
    }
  }
}

// 导出单例
export const guideModule = new GuideModule();
